import {
  Button,
  Container,
  FormControl,
  FormLabel, HStack, Input,
  Spinner, Text,
} from "@chakra-ui/react";
import {AxiosResponse} from "axios";
import { Field, Form, Formik } from "formik";
import React, {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {Navigate, useLocation, useNavigate} from "react-router-dom";

import {getCurrentUser as apiGetCurrentUser, submitUserForm} from "../Api";
import {
  useAuthentication,
} from "../hooks/AuthenticationProvider";
import {disableBlurProtection} from "../hooks/BlurProtection";
import {useTheme} from "../hooks/ThemeProvider";
import {Paths} from "../model/Constants";
import {UserDto} from "../model/Dto";
import {authenticationKeys} from "../QueryKeys";
import {enableKeyCommands, isInAvataiSite} from "../Utils";

type FormParams = {
  ID: string;
  ID2: string;
  name: string;
  lastName1: string;
  lastName2: string;
  email: string;
  region: string;
};

const IntroForm = () => {
  const theme = useTheme().theme;
  const [upperCaseName, setUpperCaseName] = useState('');
  const [upperCaseLastName1, setUpperCaseLastName1] = useState('');
  const [upperCaseLastName2, setUpperCaseLastName2] = useState('');
  const [upperCaseRegion, setUpperCaseRegion] = useState('');
  const [upperCaseEmail, setUpperCaseEmail] = useState('');
  const [ID, setID] = useState('');
  const [ID2, setID2] = useState('');
  const [error, setError] = useState('');

  const invalidID = !ID.trim() || ID !== ID2;

  const authentication = useAuthentication();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if ( isInAvataiSite())
    navigate(Paths.HOME, { replace: true });

  const currentUserQuery = useQuery<AxiosResponse<UserDto>, Error>(
    authenticationKeys.currentUser(),
    apiGetCurrentUser
  );

  disableBlurProtection();
  enableKeyCommands();

  const submitForm = useMutation<AxiosResponse<boolean>, Error, FormParams>(
    (params: FormParams) => {
      setError("");
      const fullName = upperCaseName+" "+upperCaseLastName1+" "+upperCaseLastName2;
      return submitUserForm(ID.trim(), fullName.trim(), upperCaseEmail.trim(), upperCaseRegion.trim());
    },
    {
      onSuccess: (TData) => {
        if (!TData.data)
          setError("La licencia ya fue utilizada. Por favor utiliza otra");

        return queryClient.invalidateQueries(authenticationKeys.currentUser());
      },
    }
  );

  if (
    !currentUserQuery ||
    currentUserQuery.isLoading
  ) {
    return <Spinner />;
  }
  if (
    currentUserQuery.isError || (currentUserQuery.data &&
      currentUserQuery.data?.status !== 401 &&
      currentUserQuery.data.data &&
      currentUserQuery.data.data.name)
  ) {
    return <Navigate to={(Paths.HOME)}
                     state={{from: location}} replace/>;
  } else
  return (
    <Container maxW="container.sm">
      <Formik
        initialValues={
          {
            ID: "",
            ID2: "",
            name: "",
            lastName1: "",
            lastName2: "",
            email: "",
            region: "",
          } as FormParams
        }
        onSubmit={(values: FormParams) =>
          submitForm.mutate(values)
        }
      >
        <Form>
          <HStack>
            <Field name="ID">
              {({ field }: any) => (
                <FormControl isRequired isInvalid={invalidID}>
                  <FormLabel htmlFor="ID">Identificación</FormLabel>
                  <Input {...field} id="ID"
                         onChange={(e)=>setID(e.target.value)}
                         value={ID}
                         onPaste={(e)=>{
                           e.preventDefault()
                           return false;
                         }}
                         onCopy={(e)=>{
                           e.preventDefault()
                           return false;
                         }}  />
                </FormControl>
              )}
            </Field>
            <Field name="ID2">
              {({ field }: any) => (
                <FormControl isRequired isInvalid={invalidID}>
                  <FormLabel htmlFor="ID2">Confirmar identificación</FormLabel>
                  <Input {...field} id="ID2"
                         onChange={(e)=>setID2(e.target.value)}
                         value={ID2}
                         onPaste={(e)=>{
                           e.preventDefault()
                           return false;
                         }}
                         onCopy={(e)=>{
                           e.preventDefault()
                           return false;
                         }}  />
                </FormControl>
              )}
            </Field>
          </HStack>
          <HStack>
            <Field name="name">
              {({ field }: any) => (
                <FormControl isRequired>
                  <FormLabel htmlFor="name">Nombre</FormLabel>
                  <Input {...field} id="name"
                         onChange={(e)=>setUpperCaseName(e.target.value?.toUpperCase())}
                         value={upperCaseName}/>
                </FormControl>
              )}
            </Field>
            <Field name="lastName1">
              {({ field }: any) => (
                <FormControl isRequired>
                  <FormLabel htmlFor="lastName1">Apellido 1</FormLabel>
                  <Input {...field} id="lastName1"
                         onChange={(e)=>setUpperCaseLastName1(e.target.value?.toUpperCase())}
                         value={upperCaseLastName1}/>
                </FormControl>
              )}
            </Field>
            <Field name="lastName2">
              {({ field }: any) => (
                <FormControl>
                  <FormLabel htmlFor="lastName2">Apellido 2</FormLabel>
                  <Input {...field} id="lastName2"
                         onChange={(e)=>setUpperCaseLastName2(e.target.value?.toUpperCase())}
                         value={upperCaseLastName2}/>
                </FormControl>
              )}
            </Field>
          </HStack>
          <HStack>
            <Field name="email">
              {({ field }: any) => (
                <FormControl isRequired>
                  <FormLabel htmlFor="email">Correo</FormLabel>
                  <Input {...field} id="email" type='email'
                         onChange={(e)=>setUpperCaseEmail(e.target.value?.toUpperCase())}
                         value={upperCaseEmail}/>
                </FormControl>
              )}
            </Field>
            <Field name="region">
              {({ field }: any) => (
                <FormControl isRequired>
                  <FormLabel htmlFor="region">Sede</FormLabel>
                  <Input {...field} id="region"
                         onChange={(e)=>setUpperCaseRegion(e.target.value?.toUpperCase())}
                         value={upperCaseRegion}/>
                </FormControl>
              )}
            </Field>
          </HStack>
          <Text>{error}</Text>
          <Button
            mt={4}
            colorScheme={theme.COLOR_SCHEME}
            type="submit"
            isLoading={authentication.loginMutation?.isLoading}
            disabled={invalidID}
            _disabled={{
              cursor: 'default',
              opacity: '0.4',
            }}
          >
            Confirmar
          </Button>
        </Form>
      </Formik>
    </Container>
  );
};

export default IntroForm;
